/* eslint-disable react-hooks/rules-of-hooks */

import isFunction from "lodash/fp/isFunction";
import React, { useState, useMemo, useCallback } from "react";
import Spinner from "shared/components/common/Spinner";
import { type PillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import { type InvoiceItemView } from "shared/mappers/database/accounting/invoice/invoice-item";
import { useHubCheckoutContext } from "hub/src/pages/checkout/context/HubCheckoutContext";
import { HubCheckoutInvoiceItemContext } from "hub/src/pages/checkout/context/HubCheckoutInvoiceItemContext";
import { HubCheckoutInvoiceItemDetails } from "hub/src/pages/checkout/summary/HubCheckoutInvoiceItemDetails";
import { HubCheckoutInvoiceSum } from "hub/src/pages/checkout/summary/HubCheckoutInvoiceSum";
import { HubCheckoutPrintInvoiceButton } from "hub/src/pages/checkout/summary/HubCheckoutPrintInvoiceButton";

export const HubCheckoutSummary = () => {
  const { invoice, invoiceIsLoading } = useHubCheckoutContext();
  const [selectedInvoiceItem, setSelectedInvoiceItem] =
    useState<InvoiceItemView | null>(null);
  const [deleteInvoiceItemModalOpen, setDeleteInvoiceItemModalOpen] =
    useState<InvoiceItemView | null>(null);

  const { getControl } = useMultiModalControls();

  const discountControls = useMemo(() => {
    return invoice?.invoiceItems?.map((item) => {
      return getControl(`${item.invoiceItemId}`);
    });
  }, [invoice?.invoiceItems, getControl]);

  if (invoiceIsLoading) {
    return <Spinner />;
  }

  return (
    <>
      <div
        className="container-light border-r border-neutral-mid-50 pt-4.5 
        overflow-y-auto md:overflow-y-visible"
      >
        <h1 className="sr-only">Order information</h1>
        <section aria-labelledby="summary-heading">
          <HubCheckoutPrintInvoiceButton />

          <div className="flex flex-col space-y-2 pt-3">
            {invoice?.invoiceItems?.map(
              (invoiceItem: InvoiceItemView, index: number) => {
                const discountCodeModalControl = discountControls![index];

                const openDiscountCodeModal = (item: InvoiceItemView) => {
                  setSelectedInvoiceItem(item);
                  discountCodeModalControl.setIsOpen(true);
                };

                return (
                  <HubCheckoutInvoiceItemContext.Provider
                    key={invoiceItem.invoiceItemId}
                    value={{
                      index,
                      invoiceItem,
                      selectedInvoiceItem,
                      setSelectedInvoiceItem,
                      deleteInvoiceItemModalOpen:
                        deleteInvoiceItemModalOpen?.invoiceItemId ===
                        invoiceItem.invoiceItemId,
                      setDeleteInvoiceItemModalOpen,
                      openDiscountCodeModal,
                      discountCodeModalControl,
                    }}
                  >
                    <div className="flex flex-col">
                      <HubCheckoutInvoiceItemDetails />
                    </div>
                  </HubCheckoutInvoiceItemContext.Provider>
                );
              },
            )}
            <HubCheckoutInvoiceSum />
          </div>
        </section>
      </div>
    </>
  );
};

const useMultiModalControls = () => {
  const [state, setState] = useState<Record<string, boolean>>({});

  const getControl = useCallback(
    (key: string): PillarModalControl => {
      return {
        isOpen: state?.[key] || false,
        setIsOpen: (action) => {
          setState((prevState) => {
            const nextValue = isFunction(action)
              ? action(prevState?.[key] || false)
              : action;

            return {
              ...prevState,
              [key]: nextValue,
            };
          });
        },
        onClose: async () => {
          setState((prevState) => {
            return { ...prevState, [key]: false };
          });
        },
      };
    },
    [state],
  );

  return {
    getControl,
  };
};
